<template>
    <div class="d-flex flex-column gap-sm">
        <KvCard>
            <template #title>
                <div v-if="loading" class="d-flex justify-content-between align-items-center w-100 gap-sm">
                    <KvSkeleton style="width: 60%" height="32px" />
                    <KvSkeleton style="width: 80px; border-radius: 16px" height="24px" />
                </div>

                <div v-else-if="client" class="d-flex justify-content-between align-items-center gap-sm">
                    <KvTypography :title="`${client.first_name} ${client.last_name}`" size="huge" truncate> {{
                        client.first_name }} {{ client.last_name }}</KvTypography>
                    <div class="d-flex gap-sm align-items-center">
                        <KvChip v-if="rfmInfo?.name" class="text-nowrap"
                            :colors="{ text: rfmInfo.textColor, background: rfmInfo.color }"
                            :prepend-icon="rfmInfo.icon">
                            {{ rfmInfo.name }}
                        </KvChip>
                        <KvPopup>
                            <template #trigger="{ toggle }">
                                <KvButton prepend-icon="ui-dots-vertical" variant="tonal" @click="toggle()" />
                            </template>
                            <template #content="{ toggle }">
                                <div class="kv-popup-list">
                                    <KvButton v-if="canDelete" alignment="left" prepend-icon="ui-trash-01"
                                        variant="tonal" @click="$emit('delete-client'); toggle();">
                                        {{ t('clients-basic-delete') }}
                                    </KvButton>
                                </div>
                            </template>
                        </KvPopup>
                    </div>
                </div>
            </template>

            <template #subtitle>
                <div v-if="loading" class="mt-sm">
                    <KvSkeleton style="width: 40%" height="12px" />
                </div>

                <div v-else-if="client">
                    <KvTypography color="muted" size="extra-small" weight="medium">{{ t('clients-detail-integration_id')
                        }}</KvTypography>
                    <KvTypography size="extra-small" weight="medium">{{ client.external_id }}
                        (<!-- i18n-validator-ignore-next-line: runtime integration source -->{{ client.external_source
                        }})</KvTypography>
                </div>
            </template>

            <template #text>
                <div v-if="loading" class="d-flex flex-column gap-md">
                    <div class="d-flex justify-content-between" v-for="i in 6" :key="i">
                        <KvSkeleton style="width: 30%" height="16px" />
                        <KvSkeleton style="width: 50%" height="16px" />
                    </div>
                </div>

                <div v-else-if="client" class="d-flex flex-column gap-sm">
                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-detail-subscribed') }}</KvTypography>
                        <KvModal size="extra-small">
                            <template #trigger="{ toggle }">
                                <div class="d-flex gap-sm" role="button" @click="openSubscription(toggle)">
                                    <KvChip :color="client.subscribed_whatsapp == 1 ? 'primary' : 'muted'">{{
                                        t('clients-detail-whatsapp') }}</KvChip>
                                    <KvChip :color="client.subscribed_sms == 1 ? 'primary' : 'muted'">{{
                                        t('clients-detail-sms') }}</KvChip>
                                    <KvChip :color="client.subscribed_email == 1 ? 'primary' : 'muted'">{{
                                        t('clients-columns-email') }}</KvChip>
                                    <KvChip v-if="$module('rcs')" :color="client.subscribed_rcs == 1 ? 'primary' : 'muted'">{{
                                        t('rcs') }}</KvChip>
                                </div>
                            </template>
                            <template #title>{{ t('clients-basic-subscribed_channels') }}</template>
                            <template #body>
                                <div class="d-flex flex-column gap-md">
                                    <KvSwitch :label="t('clients-detail-whatsapp')" v-model="subscription.whatsapp"
                                        return-type="number" />
                                    <KvSwitch :label="t('clients-detail-sms')" v-model="subscription.sms"
                                        return-type="number" />
                                    <KvSwitch :label="t('clients-columns-email')" v-model="subscription.email"
                                        return-type="number" />
                                    <KvSwitch v-if="$module('rcs')" :label="t('rcs')" v-model="subscription.rcs"
                                        return-type="number" />
                                </div>
                            </template>
                            <template #footer="{ toggle }">
                                <div class="d-flex justify-content-between align-items-center w-100">
                                    <KvButton variant="outlined" @click="toggle">{{ t('clients-basic-close') }}
                                    </KvButton>
                                    <KvButton variant="filled" prepend-icon="ui-check" :loading="processingSubscription"
                                        @click="saveSubscription(toggle)">{{ t('clients-basic-save') }}</KvButton>
                                </div>
                            </template>
                        </KvModal>
                    </div>

                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-columns-phone') }}</KvTypography>
                        <KvTypography weight="medium">{{ client.whatsapp ? formatPhone(client.whatsapp) :
                            t('clients-not_informed') }}
                        </KvTypography>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-columns-email') }}</KvTypography>
                        <KvTypography weight="medium">{{ client.email ? client.email : t('clients-not_informed') }}
                        </KvTypography>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-basic-first_name') }}</KvTypography>
                        <KvTypography weight="medium">{{ client.first_name ? client.first_name :
                            t('clients-not_informed') }}</KvTypography>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-basic-last_name') }}</KvTypography>
                        <KvTypography weight="medium">{{ client.last_name ? client.last_name : t('clients-not_informed')
                            }}</KvTypography>
                    </div>
                    <div class="d-flex justify-content-between align-items-center">
                        <KvTypography color="muted">{{ t('clients-basic-birthdate') }}</KvTypography>
                        <KvTypography weight="medium">{{ client.birthdate ? new
                            Date(client.birthdate).toLocaleDateString("pt-BR") :
                            t('clients-not_informed') }}</KvTypography>
                    </div>
                </div>
            </template>

            <template #actions>
                <div v-if="loading" class="d-flex justify-content-between w-100">
                    <KvSkeleton style="width: 100px" height="36px" />
                    <KvSkeleton style="width: 36px" height="36px" />
                </div>

                <div v-else-if="client" class="d-flex justify-content-between align-items-center w-100">
                    <div class="d-flex gap-sm align-items-center">
                        <KvButton prepend-icon="ui-edit-05" variant="outlined" @click="$emit('open-client-modal')">{{
                            t('clients-detail-edit') }}</KvButton>
                    </div>
                    <KvPopup>
                        <template #trigger="{ toggle }">
                            <KvButton prepend-icon="ui-plus" @click="toggle()" />
                        </template>

                        <template #content="{ toggle }">
                            <div class="kv-popup-list">
                                <KvButton alignment="left" prepend-icon="ui-check-done-02" variant="tonal"
                                    @click="$refs.registerEvent?.openModal(); toggle();"
                                    v-if="$store.dispatch('hasPermission', 'reports')">
                                    {{ t('clients-basic-register_event') }}
                                </KvButton>
                                <KvButton alignment="left" prepend-icon="ui-cursor-click-01" variant="tonal"
                                    @click="$refs.runManualAutomation?.openModal(); toggle();"
                                    v-if="$module('automations')">
                                    {{ t('clients-basic-run_manual_automation') }}
                                </KvButton>
                                <KvButton alignment="left" prepend-icon="ui-check-done-02" variant="tonal"
                                    @click="$emit('modal-open-tasks'); toggle();">
                                    {{ t('clients-basic-create_task') }}
                                </KvButton>
                                <KvButton alignment="left" prepend-icon="ui-package" variant="tonal"
                                    @click="$emit('modal-open-order'); toggle();">
                                    {{ t('clients-basic-include_orders') }}
                                </KvButton>
                                <KvButton alignment="left" prepend-icon="ui-file-search-03" variant="tonal"
                                    @click="$emit('modal-open-forms'); toggle();">
                                    {{ t('clients-basic-create_single_form_link') }}
                                </KvButton>
                                <KvButton v-if="$pointsProgram()" alignment="left" prepend-icon="ui-coins-stacked-03"
                                    variant="tonal" @click="$emit('modal-open-points'); toggle();">
                                    {{ t('clients-basic-manage_points') }}
                                </KvButton>
                            </div>
                        </template>
                    </KvPopup>
                </div>
            </template>
        </KvCard>

        <KvCard>
            <template #title>
                <div v-if="loading" class="d-flex justify-content-between align-items-center">
                    <KvSkeleton style="width: 120px" height="20px" />
                    <KvSkeleton style="width: 100px" height="40px" />

                </div>

                <div v-else class="d-flex justify-content-between align-items-center">
                    <KvTypography weight="medium" color="secondary">{{ t('clients-basic-segments') }}</KvTypography>
                    <KvModal size="large">
                        <template #trigger="{ toggle }">
                            <KvButton variant="outlined" prepend-icon="ui-eye" icon-size="3" @click="toggle">{{
                                t('clients-basic-see_more') }}</KvButton>
                        </template>

                        <template #header>
                            <div class="d-flex justify-content-between align-items-center gap-sm">
                                <KvTypography weight="semibold" color="secondary" size="large">{{
                                    t('clients-basic-segments') }}</KvTypography>
                                <KvInput :placeholder="t('clients-basic-search')" v-model="searchSegment">
                                    <template #prepend>
                                        <Icon icon-class="ui-search-01" size="2" color="#9DA4AE" />
                                    </template>
                                </KvInput>
                            </div>
                        </template>

                        <template #body>
                            <div class="d-flex flex-wrap gap-md">
                                <KvChip v-for="segment in filteredSegments" :key="segment.id"
                                    :colors="{ background: '#F3F4F6', text: '#4D5761' }">
                                    <!-- i18n-validator-ignore-next-line: runtime segment name -->
                                    {{ segment.name }}
                                </KvChip>
                            </div>
                        </template>

                        <template #footer="{ toggle }">
                            <div class="d-flex justify-content-between align-items-center w-100">
                                <KvTypography weight="medium" color="secondary">{{ t('clients-basic-total_segments',
                                    [filteredSegments.length]) }}</KvTypography>
                                <KvButton prepend-icon="ui-x-close" variant="outlined" @click="toggle">{{
                                    t('clients-basic-close') }}</KvButton>
                            </div>
                        </template>
                    </KvModal>
                </div>
            </template>

            <template #text>
                <div v-if="loading" class="d-flex flex-wrap gap-md align-items-center">
                    <KvSkeleton style="width: 80px; border-radius: 12px" height="24px" />
                    <KvSkeleton style="width: 100px; border-radius: 12px" height="24px" />
                    <KvSkeleton style="width: 60px; border-radius: 12px" height="24px" />
                </div>

                <div v-else-if="visibleSegments.length > 0" class="d-flex flex-wrap gap-md align-items-center">
                    <KvChip v-for="segment in visibleSegments" :key="segment.id"
                        :colors="{ background: '#F3F4F6', text: '#4D5761' }">
                        <!-- i18n-validator-ignore-next-line: runtime segment name -->
                        {{ segment.name }}
                    </KvChip>

                    <KvTypography v-if="hiddenCount > 0" weight="medium" size="small" color="muted">
                        +{{ hiddenCount }}
                    </KvTypography>
                </div>
            </template>
        </KvCard>

        <template v-if="loading">
            <KvCard collapsible :default-open="!screen.isMobile">
                <template #title>
                    <KvSkeleton style="width: 150px" height="20px" />
                </template>
                <template #text>
                    <div class="d-flex flex-column gap-sm">
                        <div class="d-flex justify-content-between" v-for="i in 3" :key="i">
                            <KvSkeleton style="width: 40%" height="16px" />
                            <KvSkeleton style="width: 30%" height="16px" />
                        </div>
                    </div>
                </template>
            </KvCard>
        </template>

        <template v-else-if="client">
            <KvCard v-for="custom_field in filteredCustomFields" :key="custom_field.group_id" collapsible
                :default-open="!screen.isMobile">
                <template #title><!-- i18n-validator-ignore-next-line: runtime custom field group name -->{{
                    custom_field.group_name }}</template>
                <template #text>
                    <div class="d-flex flex-column gap-sm">
                        <div class="d-flex justify-content-between align-items-center gap-sm"
                            v-for="field in custom_field.fields" :key="field.id">
                            <KvTypography color="muted">
                                <!-- i18n-validator-ignore-next-line: runtime custom field label -->{{ field.field_name
                                }}</KvTypography>
                            <KvTypography weight="medium" truncate>
                                <custom-field-value :type="field.type" :value="field.value" />
                            </KvTypography>
                        </div>
                    </div>
                </template>
            </KvCard>
        </template>
        <register-event ref="registerEvent" v-if="client && client.id && $module('custom_events')" hide-button
            :client="client" @update="loadData">
        </register-event>
        <run-manual-automation ref="runManualAutomation" v-if="client && client.id && $module('automations')"
            :client="client" @update="loadData" />
    </div>
</template>

<script>
import { mapRfm } from "@/helpers/mapper";
import KvChip from "@/components/ui/kv-chip.vue";
import KvTypography from "@/components/ui/kv-typography.vue";
import KvButton from "@/components/form/kv-button.vue";
import KvCard from "@/components/ui/kv-card.vue";
import KvModal from "@/components/modal/modal.vue";
import KvInput from "@/components/inputs/kv-input.vue";
import KvPopup from "@/components/ui/kv-popup.vue";
import KvSkeleton from "@/components/ui/kv-skeleton.vue";
import RegisterEvent from "@/custom-events/register-event.vue";
import RunManualAutomation from "./run-manual-automation.vue";
import keviApi from "@/keviApi";
import httpStatus from "@/helpers/httpStatus";
import phoneMixin from "@/mixins/phone-mixin";

export default {
    mixins: [phoneMixin],
    components: {
        KvModal,
        KvButton,
        KvChip,
        KvTypography,
        KvCard,
        KvInput,
        KvPopup,
        KvSkeleton,
        RegisterEvent,
        RunManualAutomation
    },
    data: function () {
        return {
            searchSegment: "",
            subscription: {
                whatsapp: null,
                sms: null,
                email: null,
                rcs: null
            },
            processingSubscription: false,
        };
    },
    props: {
        client: { type: Object, default: null },
        timeline: { type: Object, default: null },
        loading: { type: Boolean, default: false },
        canDelete: { type: Boolean, default: false },
    },
    methods: {
        loadData() {
            this.$emit("loadData", false);
        },
        t(term, replaces) {
            return this.$root.t(term, replaces);
        },
        openSubscription(toggle) {
            if (!this.client) return;
            this.subscription.whatsapp = this.client.subscribed_whatsapp;
            this.subscription.email = this.client.subscribed_email;
            this.subscription.sms = this.client.subscribed_sms;
            this.subscription.rcs = this.client.subscribed_rcs;

            toggle();
        },
        async saveSubscription(toggle) {
            this.processingSubscription = true;

            try {
                const payload = {
                    subscribed_whatsapp: this.subscription.whatsapp,
                    subscribed_sms: this.subscription.sms,
                    subscribed_email: this.subscription.email,
                };
                if (this.$module('rcs')) {
                    payload.subscribed_rcs = this.subscription.rcs;
                }
                const options = keviApi.getOptions(`/client/${this.client.id}/save-subscription`, "PUT", payload);
                const response = await this.$http(options);

                if (response.status === httpStatus.OK) {
                    this.$emit("update-subscription", {
                        whatsapp: this.subscription.whatsapp,
                        sms: this.subscription.sms,
                        email: this.subscription.email,
                        rcs: this.subscription.rcs
                    });
                    toggle();
                } else {
                    this.$store.dispatch('notification/error', this.t('clients-notes-save_error', [response.statusText || '']));
                }
            } catch (error) {
                this.$store.dispatch('notification/error', this.t('clients-notes-save_error', ['']));
            } finally {
                this.processingSubscription = false;

            }
        },
    },
    computed: {
        filteredCustomFields() {
            if (!this.client || !this.client.custom_fields) return [];

            return this.client.custom_fields.reduce((acc, group) => {
                const validFields = (group.fields || []).filter(field =>
                    field.value !== null &&
                    field.value !== undefined &&
                    field.value !== ""
                );

                if (validFields.length > 0) {
                    acc.push({
                        ...group,
                        fields: validFields
                    });
                }

                return acc;
            }, []);
        },
        screen: function () {
            return this.$store.state.screen;
        },
        visibleSegments() {
            if (this.loading || !this.timeline?.segments) return [];
            return this.timeline.segments.slice(0, 4)
        },
        hiddenCount() {
            if (this.loading || !this.timeline?.segments) return 0;
            return Math.max(this.timeline.segments.length - 4, 0)
        },
        filteredSegments() {
            if (this.loading || !this.timeline?.segments) return [];
            if (!this.searchSegment) return this.timeline.segments;

            return this.timeline.segments.filter(segment =>
                segment.name.toLowerCase().includes(this.searchSegment.toLowerCase())
            );
        },
        rfmInfo() {
            if (this.loading || !this.timeline) return { name: '', color: '', textColor: '', icon: '' };
            return mapRfm(this.timeline.rfm_segment);
        },
    }
};
</script>

<style scoped lang="less"></style>
